// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2025-2026 Daragh Farrell

import type { LucideIcon } from 'lucide-react';
import {
  LayoutDashboard,
  Users,
  Wallet,
  Calendar,
  Building2,
  Mail,
  Settings,
  CreditCard,
  ClipboardList,
  Receipt,
  RotateCcw,
  ArrowLeftRight,
  FileText,
  DoorOpen,
  Ticket,
  AlertTriangle,
  CalendarDays,
  BookOpen,
  Landmark,
  Home,
  UserCircle,
  RefreshCw,
  UserCheck,
  Image,
  Download,
  ShoppingBag,
  ShoppingCart,
  Package,
  BarChart3,
  Banknote,
  Gift,
  Heart,
  Award,
  Megaphone,
  Wrench,
  Key,
  Monitor,
  PieChart,
} from 'lucide-react';
import type { ProductVariant } from '../context/VariantContext';

export interface NavItem {
  path: string;
  labelKey: string;
  icon: LucideIcon;
  requiredRoles?: string[];
  variant?: ProductVariant;
}

export interface NavGroup {
  id: string;
  labelKey: string;
  icon: LucideIcon;
  items: NavItem[];
  defaultOpen?: boolean;
}

/** Dashboard is top-level, not in a group */
export const dashboardItem: NavItem = {
  path: '/dashboard',
  labelKey: 'nav.dashboard',
  icon: LayoutDashboard,
};

export const adminNavGroups: NavGroup[] = [
  {
    id: 'members',
    labelKey: 'nav.members',
    icon: Users,
    defaultOpen: true,
    items: [
      { path: '/members', labelKey: 'nav.membersList', icon: Users },
      { path: '/membership-plans', labelKey: 'nav.plans', icon: ClipboardList, requiredRoles: ['admin'] },
      { path: '/membership-subscriptions', labelKey: 'nav.subscriptions', icon: CreditCard, requiredRoles: ['admin'] },
    ],
  },
  {
    id: 'finance',
    labelKey: 'nav.finance',
    icon: Wallet,
    items: [
      { path: '/payments', labelKey: 'nav.payments', icon: Receipt, requiredRoles: ['admin', 'treasurer'] },
      { path: '/refunds', labelKey: 'nav.refunds', icon: RotateCcw, requiredRoles: ['admin'] },
      { path: '/reconciliation', labelKey: 'nav.reconciliation', icon: ArrowLeftRight, requiredRoles: ['admin'] },
      { path: '/billing', labelKey: 'nav.billing', icon: FileText, requiredRoles: ['admin'] },
      { path: '/orders', labelKey: 'nav.orderManagement', icon: Package, requiredRoles: ['admin'] },
      { path: '/club-commission', labelKey: 'nav.commission', icon: BarChart3, requiredRoles: ['admin'] },
      { path: '/club-payouts', labelKey: 'nav.payouts', icon: Banknote, requiredRoles: ['admin'] },
      { path: '/vouchers', labelKey: 'nav.vouchers', icon: Gift, requiredRoles: ['admin'] },
      { path: '/donations', labelKey: 'nav.donations', icon: Heart, requiredRoles: ['admin'] },
    ],
  },
  {
    id: 'events',
    labelKey: 'nav.events',
    icon: Calendar,
    items: [
      { path: '/events', labelKey: 'nav.eventsList', icon: Calendar },
      { path: '/events/register', labelKey: 'nav.registerForEvents', icon: Ticket },
      { path: '/events/my-registrations', labelKey: 'nav.myRegistrations', icon: BookOpen },
      { path: '/schedule/conflicts', labelKey: 'nav.conflicts', icon: AlertTriangle },
      { path: '/timetable/terms', labelKey: 'nav.terms', icon: CalendarDays },
    ],
  },
  {
    id: 'facilities',
    labelKey: 'nav.facilities',
    icon: Building2,
    items: [
      { path: '/rooms', labelKey: 'nav.rooms', icon: DoorOpen },
      { path: '/rooms/calendar', labelKey: 'nav.roomCalendar', icon: CalendarDays },
      { path: '/reservations', labelKey: 'nav.reservations', icon: BookOpen },
      { path: '/facilities', labelKey: 'nav.facilitiesList', icon: Landmark, variant: 'v2' },
      { path: '/equipment', labelKey: 'nav.equipment', icon: Wrench },
    ],
  },
  {
    id: 'shop',
    labelKey: 'nav.shop',
    icon: ShoppingBag,
    items: [
      { path: '/products', labelKey: 'nav.productManagement', icon: Package, requiredRoles: ['admin'] },
      { path: '/shop', labelKey: 'nav.browseShop', icon: ShoppingBag },
      { path: '/my-orders', labelKey: 'nav.myOrders', icon: Receipt },
      { path: '/cart', labelKey: 'nav.cart', icon: ShoppingCart },
    ],
  },
  {
    id: 'communications',
    labelKey: 'nav.communications',
    icon: Mail,
    items: [
      { path: '/notices', labelKey: 'nav.noticeBoard', icon: Megaphone },
      { path: '/notices/manage', labelKey: 'nav.noticeManage', icon: Megaphone, requiredRoles: ['admin'] },
      { path: '/email/templates', labelKey: 'nav.emailTemplates', icon: Mail, requiredRoles: ['admin'] },
    ],
  },
  {
    id: 'settings',
    labelKey: 'nav.settings',
    icon: Settings,
    items: [
      { path: '/settings', labelKey: 'nav.clubSettings', icon: Settings, requiredRoles: ['admin'] },
      { path: '/settings/committee', labelKey: 'nav.committeeSettings', icon: UserCheck, requiredRoles: ['admin'] },
      { path: '/settings/gallery', labelKey: 'nav.gallerySettings', icon: Image, requiredRoles: ['admin'] },
      { path: '/sponsors', labelKey: 'nav.sponsors', icon: Award, requiredRoles: ['admin'] },
      { path: '/settings/tokens', labelKey: 'nav.apiTokens', icon: Key, requiredRoles: ['admin'] },
      { path: '/account/sessions', labelKey: 'nav.activeSessions', icon: Monitor },
    ],
  },
  {
    id: 'analytics',
    labelKey: 'nav.analytics',
    icon: PieChart,
    items: [
      { path: '/analytics', labelKey: 'nav.clubAnalytics', icon: PieChart, requiredRoles: ['admin'] },
    ],
  },
  {
    id: 'export',
    labelKey: 'nav.export',
    icon: Download,
    items: [
      { path: '/export', labelKey: 'nav.dataExport', icon: Download, requiredRoles: ['admin'] },
    ],
  },
];

/** Portal navigation (flat, no groups needed) */
export const portalNavItems: NavItem[] = [
  { path: '/portal', labelKey: 'nav.portal.home', icon: Home },
  { path: '/portal/profile', labelKey: 'nav.portal.profile', icon: UserCircle },
  { path: '/portal/payments', labelKey: 'nav.portal.payments', icon: CreditCard },
  { path: '/portal/subscriptions', labelKey: 'nav.portal.subscriptions', icon: RefreshCw },
  { path: '/portal/events', labelKey: 'nav.portal.events', icon: Calendar },
  { path: '/portal/timetable', labelKey: 'nav.portal.timetable', icon: CalendarDays },
  { path: '/portal/credit', labelKey: 'nav.portal.credit', icon: Wallet },
  { path: '/portal/transfers', labelKey: 'nav.portal.transfers', icon: ArrowLeftRight },
  { path: '/account', labelKey: 'nav.portal.account', icon: Settings },
];

/** Priority items shown in mobile bottom nav (max 5) */
export const mobilePriorityItems: NavItem[] = [
  { path: '/dashboard', labelKey: 'nav.dashboard', icon: LayoutDashboard },
  { path: '/members', labelKey: 'nav.membersList', icon: Users },
  { path: '/events', labelKey: 'nav.eventsList', icon: Calendar },
  { path: '/payments', labelKey: 'nav.payments', icon: Wallet, requiredRoles: ['admin', 'treasurer'] },
  { path: '/settings', labelKey: 'nav.clubSettings', icon: Settings, requiredRoles: ['admin'] },
];
